type HeroBannerSlide = HTMLElement;

let heroBannerInstanceCount = 0;

function setSlideState(slide: HeroBannerSlide, active: boolean): void {
  slide.toggleAttribute('data-active', active);
  slide.setAttribute('aria-hidden', active ? 'false' : 'true');
  slide.inert = !active;
}

function setDotState(dot: HTMLButtonElement, active: boolean): void {
  dot.toggleAttribute('data-active', active);
  dot.setAttribute('aria-current', active ? 'true' : 'false');
}

export default function heroBanner(root: HTMLElement): void {
  const slides = [...root.querySelectorAll<HeroBannerSlide>('[data-hero-slide]')];
  const headings = [...root.querySelectorAll<HTMLElement>('[data-hero-heading]')];
  const status = root.querySelector<HTMLElement>('[data-hero-status]');
  const controls = [...root.querySelectorAll<HTMLElement>('[data-hero-controls]')];
  const promos = [...root.querySelectorAll<HTMLElement>('[data-hero-promo]')];
  const controlSets = slides.map(slide => ({
    dots: [...slide.querySelectorAll<HTMLButtonElement>('[data-hero-dot]')],
    toggle: slide.querySelector<HTMLButtonElement>('[data-hero-toggle]'),
    playIcon: slide.querySelector<HTMLElement>('[data-hero-toggle-play]'),
    pauseIcon: slide.querySelector<HTMLElement>('[data-hero-toggle-pause]')
  }));
  const autoRotateMs = Number(root.dataset.autoRotateMs || '6000');
  const autoRotateEnabled = root.dataset.autoRotateEnabled !== 'false';
  const shouldReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (slides.length === 0) {
    root.setAttribute('hidden', 'true');
    return;
  }

  let activeIndex = 0;
  let intervalId: number | null = null;
  let isPaused = false;
  const canAutoRotate =
    autoRotateEnabled && !shouldReduceMotion && slides.length > 1 && autoRotateMs > 0;
  const instanceId = ++heroBannerInstanceCount;

  promos.forEach((promo, index) => {
    const title = promo.querySelector<HTMLElement>('.promo-card__title');
    const link = promo.querySelector<HTMLAnchorElement>('.promo-card__link');
    if (title && link && !promo.classList.contains('promo-card--countdown')) {
      title.id = `hero-banner-${instanceId}-promo-title-${index + 1}`;
      link.setAttribute('aria-labelledby', title.id);
    }
  });

  headings.forEach((heading, index) => {
    if (!heading.id) {
      heading.id = `hero-banner-${instanceId}-heading-${index + 1}`;
    }
  });

  const syncToggle = (): void => {
    controlSets.forEach(({ toggle, playIcon, pauseIcon }) => {
      if (!toggle) {
        return;
      }

      toggle.hidden = !canAutoRotate;
      toggle.setAttribute('aria-label', isPaused ? 'Play slide rotation' : 'Pause slide rotation');
      toggle.setAttribute('aria-pressed', isPaused ? 'true' : 'false');

      if (playIcon) {
        playIcon.hidden = !isPaused;
      }

      if (pauseIcon) {
        pauseIcon.hidden = isPaused;
      }
    });
  };

  const syncUi = (): void => {
    slides.forEach((slide, index) => setSlideState(slide, index === activeIndex));
    controlSets.forEach(({ dots }) => {
      dots.forEach((dot, index) => {
        setDotState(dot, index === activeIndex);
        dot.setAttribute('aria-label', `Go to slide ${index + 1} of ${slides.length}`);
      });
    });

    if (status) {
      status.textContent = `Slide ${activeIndex + 1} of ${slides.length}`;
    }

    const activeHeading = headings[activeIndex];
    if (activeHeading) {
      root.setAttribute('aria-labelledby', activeHeading.id);
    }

    syncToggle();
  };

  const goTo = (index: number): void => {
    activeIndex = (index + slides.length) % slides.length;
    syncUi();
  };

  const stopAutoRotate = (): void => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  };

  const startAutoRotate = (): void => {
    if (
      !canAutoRotate ||
      isPaused ||
      intervalId !== null ||
      document.querySelector('dialog[open]')
    ) {
      return;
    }

    intervalId = window.setInterval(() => {
      goTo(activeIndex + 1);
    }, autoRotateMs);
  };

  controlSets.forEach(({ dots, toggle }) => {
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        goTo(index);
        if (!isPaused) {
          stopAutoRotate();
          startAutoRotate();
        }
      });
    });

    if (toggle) {
      toggle.addEventListener('click', () => {
        isPaused = !isPaused;

        if (isPaused) {
          stopAutoRotate();
        } else {
          startAutoRotate();
        }

        syncToggle();
      });
    }
  });

  controls.forEach(control => {
    control.hidden = slides.length <= 1;
  });

  promos.forEach(promo => {
    promo
      .querySelector<HTMLButtonElement>('[data-promo-card-dismiss]')
      ?.addEventListener('click', () => {
        promos.forEach(item => {
          item.hidden = true;
        });
      });
  });

  root.addEventListener('mouseenter', stopAutoRotate);
  root.addEventListener('mouseleave', () => {
    startAutoRotate();
  });
  root.addEventListener('focusin', stopAutoRotate);
  root.addEventListener(
    'click',
    event => {
      if (event.target instanceof Element && event.target.closest('[data-modal-trigger]')) {
        stopAutoRotate();
      }
    },
    true
  );
  root.addEventListener('focusout', event => {
    const nextTarget = event.relatedTarget;

    if (!(nextTarget instanceof Node) || !root.contains(nextTarget)) {
      startAutoRotate();
    }
  });

  syncUi();
  startAutoRotate();
}
